import { type UseQueryResult } from "@tanstack/react-query";

import { useCheckFeatureFlag } from "@/shared/domain/featureFlags/hooks";
import {
  isTopicStatsRefusal,
  type SessionRecord,
  type SessionTopicStatsRun,
} from "@/shared/domain/sessions";
import {
  useSessionIngestionStatus,
  useSessionTopicStatsRun,
} from "@/shared/domain/sessions/hooks";

export interface TopicStatsRunState {
  /** Whether the session's topic stats are shown: the flag is on and the routes do not refuse its org. */
  statsShown: boolean;
  /** The latest topic stats run, as the run route last answered. */
  run: UseQueryResult<SessionTopicStatsRun, Error>;
  /** Whether that run is scheduled or running, while stats are shown. */
  generating: boolean;
  /** Whether that run failed, while stats are shown. */
  failed: boolean;
}

/**
 * Whether a session's topic stats are shown, and where the latest run that
 * computes them stands. The session header and the Topics tab both read it;
 * they share one run query, so the run is polled while either is on screen.
 *
 * The routes gate topic stats on the session's org, but the flag check also
 * passes for a user in a rollout group. It only saves the requests when the
 * flag is off; the run route's refusal decides.
 */
export function useTopicStatsRunState(
  session: SessionRecord,
): TopicStatsRunState {
  const flagOn = useCheckFeatureFlag(
    "release-session-topic-stats",
    session.org_id,
  );
  const ingestion = useSessionIngestionStatus({ session, enabled: flagOn });
  const run = useSessionTopicStatsRun({
    sessionId: session.session_id,
    announcements: Math.max(
      session.ingestion_count,
      ingestion.data?.ingestion_count ?? 0,
    ),
    enabled: flagOn,
  });
  const statsShown = flagOn && !isTopicStatsRefusal(run.error);
  const status = statsShown ? run.data?.status : undefined;
  return {
    statsShown,
    run,
    generating: status === "scheduled" || status === "running",
    failed: status === "failed",
  };
}
